import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Sorter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { CartReservedSeat } from '../SeatingPlan.js';
import { CartItem } from './CartItem.js';
export class Ticket extends AutoEncoder {
    id;
    scannedAt = null;
    createdAt = new Date();
    updatedAt = new Date();
    /**
     * Unique secret (per webshop) that is printed on the ticket and is required for lookups
     * + inside QR-code
     */
    secret;
    index;
    total;
    seat = null;
    originalSeat = null;
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Ticket.prototype, "id", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Ticket.prototype, "scannedAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Ticket.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Ticket.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Ticket.prototype, "secret", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], Ticket.prototype, "index", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], Ticket.prototype, "total", void 0);
__decorate([
    field({ decoder: CartReservedSeat, nullable: true, version: 216 })
], Ticket.prototype, "seat", void 0);
__decorate([
    field({ decoder: CartReservedSeat, nullable: true, version: 216 })
], Ticket.prototype, "originalSeat", void 0);
/**
 * Ticket extended with the order data. This is
 * required because the user doesn't have access
 * to the order. So we need to add the relevant data
 * */
export class TicketPublic extends Ticket {
    items = [];
    get isSingle() {
        return this.items.length === 1 && this.items[0].product.isTicket;
    }
    getPrice(order) {
        if (!this.isSingle) {
            if (order) {
                return order.data.totalPrice;
            }
            return Math.max(0, this.items.reduce((c, item) => c + (item.price ?? 0), 0));
        }
        const item = this.items[0];
        return (item.unitPrice ?? 0) + (this.seat?.price ?? 0);
    }
    getTitle() {
        if (!this.isSingle) {
            return $t(`%2K`);
        }
        return this.items[0].product.name;
    }
    getChangedSeatString(webshop, isCustomer) {
        if (!this.isSingle) {
            return null;
        }
        const item = this.items[0];
        if (!item || !item.product.seatingPlanId) {
            return null;
        }
        if (!this.originalSeat) {
            return null;
        }
        if (!this.seat) {
            return null;
        }
        if (this.seat.equals(this.originalSeat)) {
            return null;
        }
        const to = this.seat.getNameString(webshop, item.product);
        const from = this.originalSeat.getNameString(webshop, item.product);
        if (isCustomer) {
            return $t(`%tE`, { from, to });
        }
        return $t(`%tF`, { from, to });
    }
    getIndexDescriptionString(webshop) {
        if (!this.isSingle) {
            return '';
        }
        const description = this.getIndexDescription(webshop);
        return description.map(d => d.title + ': ' + d.value).join('\n');
    }
    getIndexDescription(webshop) {
        if (!this.isSingle) {
            return [];
        }
        const item = this.items[0];
        if (!item || !item.product.seatingPlanId) {
            return [];
        }
        const seat = this.seat;
        if (seat) {
            const r = seat.getName(webshop, item.product);
            if (r.length > 0) {
                return r;
            }
        }
        return [
            {
                title: $t(`%sA`),
                value: $t(`%tG`),
            },
        ];
    }
    getSeatingPlanId() {
        if (!this.isSingle) {
            return null;
        }
        return this.items[0].product.seatingPlanId;
    }
    getIndexText() {
        if (this.items.length !== 1) {
            if (this.total > 1) {
                return this.index + ' / ' + this.total;
            }
            return null;
        }
        const item = this.items[0];
        const nameField = item.fieldAnswers.findIndex(a => a.field.name.toLowerCase().includes('naam'));
        if (nameField !== -1) {
            return item.fieldAnswers[nameField].answer;
        }
        if (this.total > 1) {
            return this.index + ' / ' + this.total;
        }
        return null;
    }
    static sort(a, b) {
        return Sorter.stack(Sorter.byNumberValue(a.items.length, b.items.length), Sorter.byStringValue(a.items[0]?.product?.name ?? '', b.items[0]?.product?.name ?? ''), Sorter.byStringValue(a.items[0]?.id ?? '', b.items[0]?.id ?? ''), // group same options and items
        -1 * Sorter.byNumberValue(a.index, b.index));
    }
    get isValid() {
        return this.items.length > 0;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(CartItem) })
], TicketPublic.prototype, "items", void 0);
/**
 * Structure if you do have access to the order (needs proof first: be an admin or pass the order id along the request)
 */
export class TicketOrder extends Ticket {
    /**
     * The orderId is private for every ticket because it provides access to the order
     */
    orderId;
    /**
     * itemId is private because access to the order is needed to be able to look it up.
     */
    itemId = null;
    getPublic(order) {
        if (this.itemId) {
            const item = order.data.cart.items.find(i => i.id === this.itemId);
            return TicketPublic.create({
                ...this,
                items: item ? [item] : [],
            });
        }
        else {
            return TicketPublic.create({
                ...this,
                items: order.data.cart.items,
            });
        }
    }
}
__decorate([
    field({ decoder: StringDecoder })
], TicketOrder.prototype, "orderId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], TicketOrder.prototype, "itemId", void 0);
export class TicketPublicPrivate extends TicketPublic {
    /**
     * Private information
     */
    scannedBy = null;
    deletedAt = null;
    getPublic() {
        return this;
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], TicketPublicPrivate.prototype, "scannedBy", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 229 })
], TicketPublicPrivate.prototype, "deletedAt", void 0);
/**
 * Structure if you do have access to the order (needs proof first: be an admin or pass the order id along the request)
 */
export class TicketPrivate extends TicketOrder {
    /**
     * Private information
     */
    scannedBy = null;
    deletedAt = null;
    getPublic(order) {
        return TicketPublicPrivate.create({
            ...super.getPublic(order),
            scannedBy: this.scannedBy,
            deletedAt: this.deletedAt,
        });
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], TicketPrivate.prototype, "scannedBy", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 229 })
], TicketPrivate.prototype, "deletedAt", void 0);
//# sourceMappingURL=Ticket.js.map